import { getTranslatedText } from "@/features/internationalisation";
import { autoAdaptationTypeValues, autoAnalysedResilienceTypeValues } from "@/types/adaptation";
import { ReturnPeriod } from "@/types/returnPeriod";
import { getReturnPeriodLabel } from "../../utils/shared";

export const AUTO_RESILIENCE_SETTINGS_TITLE = `${getTranslatedText("terms:automatedResilience")} Settings`;
const RETURN_PERIOD_SUFFIX = ` Threshold (${getTranslatedText("terms:returnPeriod")})`;
const PATHWAY_SUFFIX = " Pathway";
const BASELINE_VALUE = "user supplied or archetype default";

type SettingValues = Record<(typeof autoAnalysedResilienceTypeValues)[number], string>;
type SettingRow = {
  termKey: string;
  suffix?: string;
  unitTermKey?: string;
  values: SettingValues;
};

export const canShowResilience = ({ isLargeSite }: { isLargeSite: boolean }) => !isLargeSite;

const returnPeriods = (low: ReturnPeriod, high: ReturnPeriod, adapted: ReturnPeriod): SettingValues => ({
  low_resilience: getReturnPeriodLabel(low),
  modern_resilience: getReturnPeriodLabel(high),
  enhanced_resilience: getReturnPeriodLabel(adapted),
});

/** Must match `autoAdaptationInputSettings` in src/utils/adaptation.ts, the values sent to the analysis. */
const settingRows: SettingRow[] = [
  {
    termKey: "hazards.extremeWind",
    suffix: RETURN_PERIOD_SUFFIX,
    values: returnPeriods(50, 200, 500),
  },
  {
    termKey: "hazards.heatFailure",
    suffix: RETURN_PERIOD_SUFFIX,
    values: returnPeriods(20, 100, 1000),
  },
  {
    termKey: "hazards.cycloneWind",
    suffix: RETURN_PERIOD_SUFFIX,
    values: returnPeriods(50, 200, 500),
  },
  {
    termKey: "foundationDesign",
    values: {
      low_resilience: "normal",
      modern_resilience: "rigid",
      enhanced_resilience: "rigid",
    },
  },
  {
    termKey: "fireProtection",
    values: {
      low_resilience: "none",
      modern_resilience: "heatEmber",
      enhanced_resilience: "flame",
    },
  },
  {
    termKey: "heightAboveGround",
    unitTermKey: "units.metres",
    values: {
      low_resilience: "0",
      modern_resilience: "Baseline asset height + 0.5 (Maximum of 1)",
      enhanced_resilience: "Baseline asset height + 1 (Maximum of 2)",
    },
  },
];

const getLabel = ({ termKey, suffix = "", unitTermKey }: SettingRow) =>
  `${getTranslatedText(`terms:${termKey}`)}${suffix}${
    unitTermKey ? ` (${getTranslatedText(`terms:${unitTermKey}`)})` : ""
  }`;

/** Column headings */
const getHeaders = () => [
  AUTO_RESILIENCE_SETTINGS_TITLE,
  ...autoAdaptationTypeValues.map((type) => `${getTranslatedText(`terms:adaptationTypes.${type}`)}${PATHWAY_SUFFIX}`),
];

/** The settings table shipped with every output showing resilience results. */
export const getSettingsRows = (): string[][] => [
  getHeaders(),
  ...settingRows.map((row) => [
    getLabel(row),
    BASELINE_VALUE,
    ...autoAnalysedResilienceTypeValues.map((type) => row.values[type]),
  ]),
];
